import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Skeleton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CodeIcon from '@mui/icons-material/Code';
import SaveIcon from '@mui/icons-material/Save';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import SftGlobalData from '../../library/SftGlobalData';
import ReportResultGrid from './ReportResultGrid';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const WebReportBuilderModal = (props) => {
    const { open, onClose } = props;

    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [catalog, setCatalog]   = useState(null); // { datasets:[], options:{} }

    const [dataset, setDataset]   = useState('sales');
    const [groupBy, setGroupBy]   = useState([]);   // [dimKey]
    const [measures, setMeasures] = useState([]);   // [measureKey]
    const [filters, setFilters]   = useState({});   // { filterKey: value }
    const [limit, setLimit]       = useState('');

    // 'config' | 'running' | 'result' | 'error'
    const [phase, setPhase]       = useState('config');
    const [runId, setRunId]       = useState(null);
    const [statusPayload, setStatusPayload] = useState(null);
    const [errorMessage, setErrorMessage]   = useState(null);

    const [sqlText, setSqlText]   = useState('');
    const [showSql, setShowSql]   = useState(false);

    const [saveName, setSaveName] = useState('');
    const [savedId, setSavedId]   = useState(null);
    const [saving, setSaving]     = useState(false);

    const pollTimer = useRef(null);
    const pollAttempts = useRef(0);
    const apiRef = useRef(props.api);
    const tRef   = useRef(props.t);
    useEffect(() => { apiRef.current = props.api; }, [props.api]);
    useEffect(() => { tRef.current   = props.t;   }, [props.t]);

    const tx = (k, fallback) => {
        const v = props.t ? props.t(k) : k;
        return (v === k || v === undefined) ? (fallback ?? k) : v;
    };

    // ── Load catalog on open ──────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        setLoadingCatalog(true);
        SftGlobalData.post('webreports&action=catalog', { key: props.api, lang: props.lang }).then(response => {
            if (typeof response.error === 'undefined' && response?.data) {
                setCatalog(response.data);
            } else {
                setErrorMessage(response?.message || tx('webreport_error', 'Could not load report builder'));
                setPhase('error');
            }
            setLoadingCatalog(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, props.lang]);

    // ── Initialize whenever the modal opens (saved config → load it, else fresh) ──
    useEffect(() => {
        if (!open) return;
        // Reset run/phase state for a clean session.
        setPhase('config');
        setRunId(null);
        setStatusPayload(null);
        setErrorMessage(null);
        setSqlText('');
        setShowSql(false);
        setSaving(false);
        pollAttempts.current = 0;
        if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }

        const cfg = props.initialConfig;
        if (cfg && props.initialDataset) {
            setDataset(props.initialDataset);
            setGroupBy(Array.isArray(cfg.group_by) ? cfg.group_by : []);
            setMeasures(Array.isArray(cfg.measures) ? cfg.measures : []);
            setFilters(cfg.filters && typeof cfg.filters === 'object' ? cfg.filters : {});
            setLimit(cfg.limit ? String(cfg.limit) : '');
            setSavedId(props.initialSavedId ?? null);
            setSaveName(props.initialName ?? '');
        } else {
            setDataset('sales'); setGroupBy([]); setMeasures([]); setFilters({}); setLimit('');
            setSavedId(null); setSaveName('');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, props.initialSavedId]);

    const currentDataset = useMemo(
        () => (catalog?.datasets || []).find(d => d.key === dataset) || null,
        [catalog, dataset]
    );

    const dimsByKey = useMemo(() => {
        const map = {};
        (currentDataset?.dimensions || []).forEach(d => { map[d.key] = d; });
        return map;
    }, [currentDataset]);
    const measuresByKey = useMemo(() => {
        const map = {};
        (currentDataset?.measures || []).forEach(m => { map[m.key] = m; });
        return map;
    }, [currentDataset]);

    const changeDataset = (next) => {
        if (!next || next === dataset) return;
        setDataset(next);
        setGroupBy([]); setMeasures([]); setFilters({}); setLimit('');
        setSavedId(null);
    };

    const setFilter = (key, value) => setFilters(s => ({ ...s, [key]: value }));

    const buildPayload = () => ({
        key: props.api,
        lang: props.lang,
        dataset,
        group_by: groupBy,
        measures,
        filters,
        limit: limit ? Number(limit) : undefined,
    });

    const canRun = groupBy.length > 0 || measures.length > 0;

    // ── Run + poll ────────────────────────────────────────────────────────────
    const runReport = () => {
        setPhase('running');
        setErrorMessage(null);
        setStatusPayload(null);
        pollAttempts.current = 0;
        SftGlobalData.post('webreports&action=execute', { ...buildPayload(), sft_user_id: props.sftUserId || '' }).then(response => {
            if (typeof response.error === 'undefined' && response?.data?.run_id) {
                setRunId(response.data.run_id);
                schedulePoll(response.data.run_id, 600);
            } else {
                setErrorMessage(response?.message || tx('webreport_error', 'Error'));
                setPhase('error');
            }
        });
    };

    const schedulePoll = useCallback((id, delayMs) => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = setTimeout(() => pollStatus(id), delayMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pollStatus = (id) => {
        SftGlobalData.post('webreports&action=status', { key: apiRef.current, run_id: id }).then(response => {
            const t = tRef.current || ((k) => k);
            if (typeof response.error !== 'undefined' || !response?.data) {
                setErrorMessage(response?.message || t('webreport_error') || 'Error');
                setPhase('error');
                return;
            }
            const data = response.data;
            setStatusPayload(data);
            if (data.status === 'done')      { setPhase('result'); return; }
            if (data.status === 'error')     { setErrorMessage(data.error || t('webreport_error') || 'Error'); setPhase('error'); return; }
            if (data.status === 'cancelled') { setPhase('config'); return; }
            pollAttempts.current += 1;
            const delay = pollAttempts.current < 10 ? 800 : pollAttempts.current < 30 ? 2000 : 5000;
            schedulePoll(id, delay);
        });
    };

    const cancelRun = () => {
        if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
        if (runId) {
            SftGlobalData.post('webreports&action=cancel', { key: apiRef.current, run_id: runId }).finally(() => {
                setPhase('config'); setRunId(null);
            });
        } else {
            setPhase('config');
        }
    };

    // ── View SQL ──────────────────────────────────────────────────────────────
    const loadSqlPreview = () => {
        if (showSql) { setShowSql(false); return; }
        SftGlobalData.post('webreports&action=preview', buildPayload()).then(response => {
            if (typeof response.error === 'undefined' && response?.data?.sql) {
                setSqlText(response.data.sql);
            } else {
                setSqlText(response?.message || tx('webreport_error', 'Error'));
            }
            setShowSql(true);
        });
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const saveReport = () => {
        if (!saveName.trim()) return;
        setSaving(true);
        SftGlobalData.post('webreports&action=saveDefinition', {
            key: props.api,
            id: savedId || undefined,
            name: saveName.trim(),
            dataset,
            config: { group_by: groupBy, measures, filters, limit: limit ? Number(limit) : undefined },
            sft_user_id: props.sftUserId || '',
        }).then(response => {
            setSaving(false);
            if (typeof response.error === 'undefined') {
                if (response?.data?.id) setSavedId(response.data.id);
                props.notify && props.notify(tx('webreport_saved', 'Report saved'), 4000, 'top-center', props.theme, 3);
                props.onSaved && props.onSaved();
            } else {
                props.notify && props.notify(response?.message || tx('webreport_error', 'Error'), 6000, 'top-center', props.theme, 1);
            }
        });
    };

    // ── Render filter control ─────────────────────────────────────────────────
    const renderFilter = (f) => {
        const value = filters[f.key];
        if (f.type === 'date') {
            return (
                <DatePicker
                    key={f.key}
                    label={f.label}
                    value={value ? dayjs(value) : null}
                    onChange={(v) => setFilter(f.key, v && v.isValid && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
            );
        }
        if (f.type === 'multiselect') {
            const opts = (catalog?.options?.[f.optionSource]) || [];
            const selectedVals = Array.isArray(value) ? value : [];
            const selectedOpts = opts.filter(o => selectedVals.includes(o.value));
            return (
                <Autocomplete
                    key={f.key}
                    multiple
                    size="small"
                    options={opts}
                    value={selectedOpts}
                    onChange={(_, newVal) => setFilter(f.key, newVal.map(o => o.value))}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    getOptionLabel={(o) => (o.label ? `${o.value} — ${o.label}` : String(o.value))}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip size="small" label={option.label || option.value} {...getTagProps({ index })} key={option.value} />
                        ))
                    }
                    renderInput={(params) => <TextField {...params} label={f.label} />}
                />
            );
        }
        if (f.type === 'bool' || f.type === 'bool_expr') {
            return (
                <FormControlLabel
                    key={f.key}
                    control={<Checkbox checked={!!value} onChange={(e) => setFilter(f.key, e.target.checked)} />}
                    label={f.label}
                />
            );
        }
        if (f.type === 'numbermin' || f.type === 'numbermax') {
            return (
                <TextField
                    key={f.key}
                    label={f.label}
                    type="number"
                    size="small"
                    fullWidth
                    value={value ?? ''}
                    onChange={(e) => setFilter(f.key, e.target.value)}
                />
            );
        }
        // text
        return (
            <TextField
                key={f.key}
                label={f.label}
                size="small"
                fullWidth
                value={value ?? ''}
                onChange={(e) => setFilter(f.key, e.target.value)}
            />
        );
    };

    const isNarrow = (props.screenWidth ?? 1200) < 700;
    const reportTitle = saveName || tx('webreport_title', 'Report builder');

    // ── Config panel ──────────────────────────────────────────────────────────
    const renderConfig = () => {
        if (loadingCatalog || !catalog) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
        }
        const dimOptions = currentDataset?.dimensions || [];
        const measureOptions = currentDataset?.measures || [];
        const filterDefs = currentDataset?.filters || [];

        return (
            <Stack spacing={2.5}>
                {/* Dataset picker */}
                <Box>
                    <Typography variant="overline" color="text.secondary">{tx('webreport_dataset', 'Dataset')}</Typography>
                    <Box>
                        <ToggleButtonGroup
                            color="primary"
                            value={dataset}
                            exclusive
                            size="small"
                            onChange={(_, v) => changeDataset(v)}
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {(catalog.datasets || []).map(d => (
                                <ToggleButton key={d.key} value={d.key}>{d.label}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                <Divider />

                {/* Group by */}
                <Box>
                    <Typography variant="overline" color="text.secondary">{tx('webreport_groupBy', 'Group by')}</Typography>
                    <Autocomplete
                        multiple
                        size="small"
                        options={dimOptions}
                        value={groupBy.map(k => dimsByKey[k]).filter(Boolean)}
                        onChange={(_, newVal) => setGroupBy(newVal.map(o => o.key))}
                        isOptionEqualToValue={(o, v) => o.key === v.key}
                        getOptionLabel={(o) => o.label || o.key}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip size="small" color="primary" variant="outlined" label={`${index + 1}. ${option.label}`} {...getTagProps({ index })} key={option.key} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} placeholder={tx('webreport_groupByHint', 'Add dimensions…')} />}
                    />
                </Box>

                {/* Measures */}
                <Box>
                    <Typography variant="overline" color="text.secondary">{tx('webreport_measures', 'Measures')}</Typography>
                    <Autocomplete
                        multiple
                        size="small"
                        options={measureOptions}
                        value={measures.map(k => measuresByKey[k]).filter(Boolean)}
                        onChange={(_, newVal) => setMeasures(newVal.map(o => o.key))}
                        isOptionEqualToValue={(o, v) => o.key === v.key}
                        getOptionLabel={(o) => o.label || o.key}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip size="small" color="success" variant="outlined" label={option.label} {...getTagProps({ index })} key={option.key} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} placeholder={tx('webreport_measuresHint', 'Add measures…')} />}
                    />
                </Box>

                <Divider />

                {/* Filters */}
                <Box>
                    <Typography variant="overline" color="text.secondary">{tx('webreport_filters', 'Filters')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
                        {filterDefs.map(renderFilter)}
                    </Box>
                </Box>

                <Divider />

                {/* Options: limit + save */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label={tx('webreport_limit', 'Row limit')}
                        type="number"
                        size="small"
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        sx={{ width: 140 }}
                    />
                    <TextField
                        label={tx('webreport_saveName', 'Report name')}
                        size="small"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        sx={{ minWidth: 200, flex: '1 1 200px' }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        disabled={!saveName.trim() || saving}
                        onClick={saveReport}
                    >
                        {savedId ? tx('webreport_update', 'Update') : tx('webreport_save', 'Save')}
                    </Button>
                </Box>

                {/* SQL preview */}
                <Collapse in={showSql}>
                    <TextField
                        label="SQL"
                        value={sqlText}
                        multiline
                        minRows={4}
                        maxRows={14}
                        fullWidth
                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                </Collapse>
            </Stack>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => { if (reason === 'backdropClick' && phase === 'running') return; onClose && onClose(); }}
            fullWidth
            maxWidth={phase === 'result' ? 'xl' : 'md'}
            PaperProps={{
                sx: {
                    m: isNarrow ? 1 : 4,
                    width: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    maxHeight: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    height: phase === 'result' ? (isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)') : undefined,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                <span>{reportTitle}</span>
                <IconButton size="small" onClick={onClose} disabled={phase === 'running'}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {phase === 'config' && renderConfig()}

                {phase === 'running' && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <CircularProgress size={22} />
                            <Typography variant="body2">{tx('webreport_running', 'Running report…')}</Typography>
                        </Stack>
                        <LinearProgress />
                        {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="text" height={28} />)}
                    </>
                )}

                {phase === 'result' && statusPayload && (
                    <ReportResultGrid
                        columns={statusPayload.columns || []}
                        rows={statusPayload.rows || []}
                        truncated={!!statusPayload.truncated}
                        rowCount={statusPayload.row_count || 0}
                        t={props.t}
                        lang={props.lang}
                        theme={props.theme}
                        reportTitle={reportTitle}
                    />
                )}

                {phase === 'error' && (
                    <Alert severity="error">{errorMessage || tx('webreport_error', 'Error')}</Alert>
                )}
            </DialogContent>

            <DialogActions>
                {phase === 'config' && (
                    <>
                        <Tooltip title={tx('webreport_viewSql', 'View SQL')}>
                            <span>
                                <Button startIcon={<CodeIcon />} onClick={loadSqlPreview} disabled={!canRun}>
                                    {showSql ? tx('webreport_hideSql', 'Hide SQL') : tx('webreport_viewSql', 'View SQL')}
                                </Button>
                            </span>
                        </Tooltip>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={onClose}>{tx('reports_close', 'Close')}</Button>
                        <Button variant="contained" startIcon={<PlayArrowIcon />} disabled={!canRun} onClick={runReport}>
                            {tx('reports_run', 'Run')}
                        </Button>
                    </>
                )}
                {phase === 'running' && (
                    <Button color="warning" onClick={cancelRun}>{tx('reports_cancel', 'Cancel')}</Button>
                )}
                {phase === 'result' && (
                    <>
                        <Button onClick={() => { setPhase('config'); setStatusPayload(null); setRunId(null); }}>
                            {tx('webreport_edit', 'Edit')}
                        </Button>
                        <Button variant="contained" onClick={onClose}>{tx('reports_close', 'Close')}</Button>
                    </>
                )}
                {phase === 'error' && (
                    <>
                        <Button onClick={() => setPhase('config')}>{tx('reports_back', 'Back')}</Button>
                        <Button variant="contained" onClick={onClose}>{tx('reports_close', 'Close')}</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default WebReportBuilderModal;
